// components/invoices/InvoiceDetailsModal.tsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ar, enGB, fr } from 'date-fns/locale';
import {
    X, FileText, Download, Printer, CheckCircle, Calendar,
    Car, User, DollarSign, AlertTriangle, Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { invoiceService } from '../../services/invoiceService';
import { useLanguage } from '../../hooks/useLanguage';

interface InvoiceItem {
    reference: string;
    description: string;
    descriptionArabic?: string;
    descriptionEnglish?: string;
    quantity: number;
    unitPriceHT: number;
    vatRate: number;
    discountPercent: number;
    totalHT: number;
    totalTTC: number;
    vatAmount: number;
    isAddedAfterQuote: boolean;
}

interface Invoice {
    _id: string;
    invoiceNumber: string;
    inspectionId: string;
    quoteId: string;
    garageId: string;
    status: 'draft' | 'finalized' | 'paid' | 'cancelled';
    items: InvoiceItem[];
    totalHT: number;
    totalVAT: number;
    totalDiscount: number;
    totalTTC: number;
    quoteAmount: number;
    additionalAmount: number;
    notes?: string;
    notesArabic?: string;
    notesEnglish?: string;
    garageNotes?: string;
    currency: string;
    sellerInfo: {
        name: string;
        nameArabic?: string;
        nameEnglish?: string;
        vatNumber: string;
        address: { street: string; city: string; postalCode: string; countryCode: string; };
        phoneNumber: string;
    };
    customerInfo: { name: string; phoneNumber: string; address: string; };
    paymentTerms: string;
    paymentTermsArabic?: string;
    paymentMethod: string;
    completedAt: Date;
    createdAt: Date;
    updatedAt: Date;
    zatcaTechnicalData?: {
        uuid: string;
        invoiceTypeCode: string;
        invoiceHash?: string;
        qrCode?: string;
        zatcaStatus?: string;
    };
    inspectionDetails?: {
        _id: string;
        vehicleBrand: string;
        vehicleModel: string;
        vehicleYear: string;
        clientName: string;
        clientPhone: string;
        status: string;
    };
}

interface InvoiceDetailsModalProps {
    invoiceId: string;
    onClose: () => void;
}

export const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({ invoiceId, onClose }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<boolean>(false);

    const getDateLocale = () => currentLanguage === 'fr' ? fr : currentLanguage === 'ar' ? ar : enGB;
    const formatCurrency = (amount: number) => `${amount.toFixed(2)} ${invoice?.currency || '€'}`;

    useEffect(() => {
        const fetchInvoiceDetails = async () => {
            try {
                setLoading(true);
                const response = await invoiceService.getInvoiceDetails(invoiceId);
                setInvoice(response);
                setError(null);
            } catch (err: any) {
                console.error('Error fetching invoice details:', err);
                setError(err.response?.data?.message || (t('invoiceDetails.loadError')));
            } finally {
                setLoading(false);
            }
        };
        fetchInvoiceDetails();
    }, [invoiceId, currentLanguage]);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const success = await invoiceService.downloadInvoicePDF(invoiceId);
            if (success) toast.success(t('invoiceDetails.downloaded'));
            else toast.error(t('invoiceDetails.downloadError'));
        } catch (err) {
            console.error('Error downloading invoice:', err);
            toast.error(t('invoiceDetails.downloadError'));
        } finally {
            setDownloading(false);
        }
    };

    const getStatusLabel = (status: string) => {
        const keys: { [key: string]: string } = {
            draft: 'statusDraft',
            finalized: 'statusFinalized',
            paid: 'statusPaid',
            cancelled: 'statusCancelled'
        };
        return keys[status] ? t(`invoiceDetails.${keys[status]}`) : status;
    };

    const getStatusColor = (status: string) => {
        const colors: { [key: string]: string } = {
            draft: 'bg-gray-100 text-gray-800',
            finalized: 'bg-green-100 text-green-800',
            paid: 'bg-blue-100 text-blue-800',
            cancelled: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                <div className={`bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl ${isRTL ? 'rtl' : 'ltr'}`}>
                    <div className="flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !invoice) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                <div className={`bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl ${isRTL ? 'rtl' : 'ltr'}`}>
                    <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <h2 className="text-xl font-semibold text-gray-800">{t('invoiceDetails.error')}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
                    </div>
                    <p className="text-red-600">{error || (t('invoiceDetails.loadError'))}</p>
                    <div className={`mt-6 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                        <button onClick={onClose} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                            {t('invoiceDetails.close')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50">
            <div className={`
                bg-white w-full h-full overflow-y-auto
                sm:rounded-lg sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:m-4
                ${isRTL ? 'rtl' : 'ltr'}
            `}>
                {/* Header */}
                <div className="flex justify-between items-start p-4 sm:p-6 border-b sticky top-0 bg-white z-10" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h2 className="text-base sm:text-xl font-semibold text-gray-800">
                                {t('invoiceDetails.title')} #{invoice.invoiceNumber}
                            </h2>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
                                    {getStatusLabel(invoice.status)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {t('invoiceDetails.issueDate')} {format(new Date(invoice.createdAt), 'PPP', { locale: getDateLocale() })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* ZATCA Badge */}
                    {invoice.zatcaTechnicalData?.zatcaStatus === 'CLEARED' && (
                        <div className={`bg-green-50 border border-green-200 p-3 rounded-lg flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <h3 className="font-medium text-green-800 text-sm">
                                {t('invoiceDetails.certified')}
                            </h3>
                        </div>
                    )}

                    {/* Vehicle & Client — stack on mobile */}
                    {invoice.inspectionDetails && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                                <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <Car className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                    <h3 className="font-medium text-sm sm:text-base">{t('invoiceDetails.vehicleInfo')}</h3>
                                </div>
                                <p className="text-sm">
                                    <span className="font-medium">{invoice.inspectionDetails.vehicleBrand} {invoice.inspectionDetails.vehicleModel}</span>
                                    {' '}({invoice.inspectionDetails.vehicleYear})
                                </p>
                            </div>
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                                <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                    <h3 className="font-medium text-sm sm:text-base">{t('invoiceDetails.client')}</h3>
                                </div>
                                <p className="text-sm">{invoice.customerInfo.name}</p>
                                <p className="text-sm text-gray-600">{invoice.customerInfo.phoneNumber}</p>
                            </div>
                        </div>
                    )}

                    {/* Alert additional work */}
                    {invoice.additionalAmount > 0 && (
                        <div className={`bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-medium text-yellow-800 text-sm">{t('invoiceDetails.additionalWork')}</h3>
                                <p className="text-xs text-yellow-700 mt-1">
                                    {t('invoiceDetails.additionalWorkDesc', { amount: formatCurrency(invoice.additionalAmount) })}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Items table — horizontal scroll */}
                    <div>
                        <h3 className="font-medium text-gray-700 mb-3 text-sm sm:text-base">
                            {t('invoiceDetails.serviceDetails')}
                        </h3>
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                            <div className="inline-block min-w-full px-4 sm:px-0">
                                <table className={`min-w-full bg-white rounded-lg border text-sm ${isRTL ? 'rtl' : ''}`}>
                                    <thead className="bg-green-50">
                                    <tr>
                                        {[
                                            t('invoiceDetails.colType'),
                                            t('invoiceDetails.colDescription'),
                                            t('invoiceDetails.colQty'),
                                            t('invoiceDetails.colUnitPrice'),
                                            t('invoiceDetails.colVat'),
                                            t('invoiceDetails.colTotal'),
                                        ].map((label, i) => (
                                            <th key={i} className={`py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b ${i < 2 ? (isRTL ? 'text-right' : 'text-left') : 'text-right'}`}>
                                                {label}
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                    {invoice.items.map((item, index) => (
                                        <tr key={index} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                                            <td className={`py-2 px-3 text-sm ${isRTL ? 'text-right' : ''}`}>
                                                {item.isAddedAfterQuote ? (
                                                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full whitespace-nowrap">
                                                            🔧 {t('invoiceDetails.tagAdded')}
                                                        </span>
                                                ) : (
                                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                                                            ✓ {t('invoiceDetails.tagQuote')}
                                                        </span>
                                                )}
                                            </td>
                                            <td className={`py-2 px-3 text-sm font-medium ${isRTL ? 'text-right' : ''}`}>{item.description}</td>
                                            <td className="py-2 px-3 text-sm text-right">{item.quantity}</td>
                                            <td className="py-2 px-3 text-sm text-right whitespace-nowrap">{formatCurrency(item.unitPriceHT)}</td>
                                            <td className="py-2 px-3 text-sm text-right">{item.vatRate}%</td>
                                            <td className="py-2 px-3 text-sm text-right font-medium whitespace-nowrap">{formatCurrency(item.totalTTC)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="border-2 border-green-400 bg-green-50 p-4 sm:p-6 rounded-lg">
                        <div className="grid grid-cols-1 gap-2 sm:gap-3 max-w-md ml-auto text-sm sm:text-base">
                            <div className="flex justify-between">
                                <span className="text-gray-700">{t('invoiceDetails.initialQuote')}</span>
                                <span className="font-medium">{formatCurrency(invoice.quoteAmount)}</span>
                            </div>
                            {invoice.additionalAmount > 0 && (
                                <div className="flex justify-between text-orange-600">
                                    <span>{t('invoiceDetails.additionalLabel')}</span>
                                    <span className="font-medium">+{formatCurrency(invoice.additionalAmount)}</span>
                                </div>
                            )}
                            <div className="border-t pt-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-700">{t('invoiceDetails.totalExclVat')}</span>
                                    <span className="font-medium">{formatCurrency(invoice.totalHT)}</span>
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-700">{t('invoiceDetails.vat')}</span>
                                <span className="font-medium">{formatCurrency(invoice.totalVAT)}</span>
                            </div>
                            <div className="border-t-2 pt-2 sm:pt-3 flex justify-between items-baseline">
                                <span className="text-base sm:text-xl font-bold">{t('invoiceDetails.finalTotal')}</span>
                                <span className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(invoice.totalTTC)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {invoice.notes && (
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                            <h3 className="font-medium text-gray-700 mb-2 text-sm sm:text-base">{t('invoiceDetails.notes')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{invoice.notes}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="border-t pt-4 flex flex-col sm:flex-row sm:justify-between gap-3" dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={handleDownload} disabled={downloading} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm">
                                <Download className="w-4 h-4" />
                                {downloading ? (t('invoiceDetails.downloading')) : (t('invoiceDetails.download'))}
                            </button>
                            <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm">
                                <Printer className="w-4 h-4" />
                                {t('invoiceDetails.print')}
                            </button>
                        </div>
                        {invoice.status === 'finalized' && (
                            <div className="bg-green-50 p-2 sm:p-3 rounded-lg text-green-700 flex items-center gap-2 text-sm">
                                <CheckCircle className="w-4 h-4" />
                                <span>{t('invoiceDetails.finalized')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};